/**
 * Role Permissions Configuration
 *
 * ⚠️ Este archivo DUPLICA lógica del backend (single source of truth en backend).
 *
 * Mirror exacto de: `rois-back/src/modules/orders/orders-state-transitions.ts`.
 *
 * Cualquier cambio aquí REQUIERE cambio coordinado en el backend, y viceversa.
 * Si no se sincroniza, el dropdown del modal "Cambiar estado" mostrará opciones
 * que el backend rechaza (UX rota) o no mostrará opciones que sí debería.
 *
 * Caso real (Tarea #10, 2026-05-08): el spec dijo "frontend cero cambios". Falso.
 * Hubo que sincronizar VALID_STATE_FLOW, isFinalState y filterStatesByWorkflow.
 *
 * Arquitectura completa (las 3 capas, mapeo exhaustivo front↔back, fórmula del
 * cálculo del dropdown, lecciones aprendidas):
 *   /context/ARCHITECTURE.md § 13 RBAC
 *
 * Matriz funcional (qué ve cada rol en cada pantalla):
 *   /docs/shared/RESUMEN-PERMISOS-POR-ROL.md
 *
 * Regla operacional para agentes IA y devs (checklist al tocar RBAC):
 *   /.claude/rules/rbac-architecture.md
 *
 * @see ../../../rois-back/src/modules/orders/orders-state-transitions.ts
 */

import { Role } from '../types/auth';
import { OrderState, OrderType } from '../types/orders';
import { getVisibleStatesForRole } from '../constants/roleStateVisibility';
import { getValidStatesForEntityType } from '../constants/entityStateFiltering';

/**
 * Configuration interface for role permissions
 */
export interface RolePermissionConfig {
  canCreate: boolean;
  allowedStates: OrderState[];
}

/**
 * State transitions configuration by role
 *
 * This configuration defines which states each role can transition orders to.
 * Must be kept in sync with backend configuration.
 */
export const STATE_PERMISSIONS_BY_ROLE: Record<Role, RolePermissionConfig> = {
  /**
   * RoomTeam (Trabajador de sala)
   * Can create orders and manage basic states
   * Can annul orders/communications
   * Cannot validate orders
   */
  [Role.RoomTeam]: {
    canCreate: true,
    allowedStates: [
      OrderState.Partial,
      OrderState.Open,
      OrderState.Managed,
      OrderState.Annulled, // RoomTeam puede anular comunicaciones/órdenes
    ],
  },

  /**
   * Tablet (Encargado de tablet)
   * Can create orders and manage workflow states
   * **CANNOT validate orders** - only Coex and SuperAdmin can validate
   * **CANNOT annul orders** - only Coex, SuperAdmin and RoomTeam can annul
   * Prepares work and marks as ready for validation (pending-validation)
   */
  [Role.Tablet]: {
    canCreate: true,
    allowedStates: [
      OrderState.Partial,
      OrderState.Open,
      OrderState.PendingWork,
      OrderState.WorkInProgress,
      OrderState.PendingValidation, // Final state for Tablet - marks work as ready for validation
      // ❌ REMOVED: OrderState.Annulled - Tablet cannot annul (only Coex/SuperAdmin/RoomTeam)
      // ❌ REMOVED: OrderState.Validated - Tablet prepares work, Coex/SuperAdmin validates
    ],
  },

  /**
   * Coex (Coordinador de Explotación)
   * Full access to all states
   */
  [Role.Coex]: {
    canCreate: true,
    allowedStates: [
      OrderState.Partial,
      OrderState.Open,
      OrderState.Managed,
      OrderState.PendingWork,
      OrderState.WorkInProgress,
      OrderState.PendingValidation,
      OrderState.PendingATValidation, // T66: Estado para validación A.T
      OrderState.Validated,
      OrderState.PendingCertification, // Actions: pendiente de cobro a Generalitat
      OrderState.Annulled,
      OrderState.End,
    ],
  },

  /**
   * SuperAdmin
   * Complete access to everything
   */
  [Role.SuperAdmin]: {
    canCreate: true,
    allowedStates: [
      OrderState.Partial,
      OrderState.Open,
      OrderState.Managed,
      OrderState.PendingWork,
      OrderState.WorkInProgress,
      OrderState.PendingValidation,
      OrderState.PendingATValidation, // T66: Estado para validación A.T
      OrderState.Validated,
      OrderState.PendingCertification, // Actions: pendiente de cobro a Generalitat
      OrderState.Annulled,
      OrderState.End,
    ],
  },

  /**
   * Capataz (introducido en main PR #48): clon funcional de Coex,
   * pero ciego a budgets (`roleCanViewBudgets` lo filtra). En la
   * máquina de estados es idéntico a Coex.
   */
  [Role.Capataz]: {
    canCreate: true,
    allowedStates: [
      OrderState.Partial,
      OrderState.Open,
      OrderState.Managed,
      OrderState.PendingWork,
      OrderState.WorkInProgress,
      OrderState.PendingValidation,
      OrderState.PendingATValidation,
      OrderState.Validated,
      OrderState.PendingCertification,
      OrderState.Annulled,
      OrderState.End,
    ],
  },

  /**
   * OperatingTeam (Equipo operativo)
   * Read-only role, cannot create or modify orders
   */
  [Role.OperatingTeam]: {
    canCreate: false,
    allowedStates: [],
  },
};

/**
 * Checks if a role can create orders
 * @param role - User role
 * @returns true if the role can create orders
 */
export function canRoleCreateOrders(role: Role): boolean {
  const config = STATE_PERMISSIONS_BY_ROLE[role];
  return config?.canCreate ?? false;
}

/**
 * Gets all allowed states for a specific role
 * @param role - User role
 * @returns Array of allowed OrderState values
 */
export function getAllowedStatesForRole(role: Role): OrderState[] {
  const config = STATE_PERMISSIONS_BY_ROLE[role];
  return config?.allowedStates ?? [];
}

/**
 * V4 (2026-04-21) — matriz rol × estado específica para SurveillanceRoute.
 * Mirror del backend en `rois-back/src/modules/surveillance-route/surveillance-route-state-validator.service.ts`
 * (`ROLE_ALLOWED_TRANSITIONS`). Documentada en V4 D5.
 *
 * Estos son los estados que cada rol puede SELECCIONAR desde el `ChangeStateModal`.
 * El workflow (desde qué estado actual se puede ir a cuál) se controla vía
 * `SR_VALID_STATE_FLOW_BY_ROLE` más abajo.
 */
const SR_STATE_PERMISSIONS_BY_ROLE: Partial<Record<Role, OrderState[]>> = {
  [Role.Tablet]: [
    // Tablet opera el cronómetro: open → in-progress ↔ paused → pending-validation.
    // NUNCA puede llegar a `end` ni `annulled` (eso es Coex/SuperAdmin).
    OrderState.Open,
    OrderState.InProgress,
    OrderState.Paused,
    OrderState.PendingValidation,
  ],
  [Role.Coex]: [
    OrderState.Open,
    OrderState.InProgress,
    OrderState.Paused,
    OrderState.PendingValidation,
    OrderState.End,
    OrderState.Annulled,
  ],
  // Capataz (paridad 2026-07-17): clon EXACTO de Coex — la matriz SR nació
  // antes que el rol y sin entrada devolvía [] (Capataz no podía tocar RVs).
  [Role.Capataz]: [
    OrderState.Open,
    OrderState.InProgress,
    OrderState.Paused,
    OrderState.PendingValidation,
    OrderState.End,
    OrderState.Annulled,
  ],
  [Role.SuperAdmin]: [
    OrderState.Open,
    OrderState.InProgress,
    OrderState.Paused,
    OrderState.PendingValidation,
    OrderState.End,
    OrderState.Annulled,
  ],
  [Role.RoomTeam]: [], // Solo lectura para RV (D5).
};

/**
 * Gets allowed states for a specific role, with type-specific overrides.
 * Tablet can annul communications but not other order types.
 * BUG #29 FIX: Validated state is only for actions - it should not appear
 * in the state picker for generic orders, communications, campaigns, etc.
 *
 * V4 (2026-04-21): `SurveillanceRoute` tiene su propia matriz (SR_STATE_PERMISSIONS_BY_ROLE).
 * El resto de entity types sigue usando STATE_PERMISSIONS_BY_ROLE genérica.
 *
 * @param role - User role
 * @param orderType - Order type (e.g., 'Communication', 'Generic', 'Action', 'surveillanceRoute')
 * @returns Array of allowed OrderState values
 */
export function getAllowedStatesForRoleAndType(
  role: Role,
  orderType: string
): OrderState[] {
  // V4 (2026-04-21): SurveillanceRoute usa matriz dedicada (valores V4 D5).
  if (orderType?.toLowerCase?.() === 'surveillanceroute') {
    return SR_STATE_PERMISSIONS_BY_ROLE[role] ?? [];
  }

  const base = getAllowedStatesForRole(role);

  // BUG #29 FIX: Validated is a state that only applies to actions.
  // For non-action entity types, exclude it from manual state transitions.
  // Orders/communications/campaigns reach this state automatically from the backend
  // when sub-elements are validated; users should not manually set it.
  // Tarea #7: PendingCertification sigue el mismo patrón — sólo aplica a Action.
  const isActionType = orderType?.toLowerCase?.() === 'action';
  const filteredBase = isActionType
    ? base
    : base.filter(
        (state) =>
          state !== OrderState.Validated &&
          state !== OrderState.PendingCertification
      );

  return filteredBase;
}

/**
 * Checks if a role can transition to a specific state
 * @param role - User role
 * @param targetState - Target OrderState
 * @returns true if the role can transition to the target state
 */
export function canRoleUseState(role: Role, targetState: OrderState): boolean {
  const allowedStates = getAllowedStatesForRole(role);
  return allowedStates.includes(targetState);
}

/**
 * Filters a list of states to only include those allowed for a role
 * @param role - User role
 * @param states - Array of states to filter
 * @returns Filtered array containing only allowed states
 */
export function filterStatesByRole(
  role: Role,
  states: OrderState[]
): OrderState[] {
  const allowedStates = getAllowedStatesForRole(role);
  return states.filter((state) => allowedStates.includes(state));
}

/**
 * Gets display-friendly error message when a role lacks permission
 * @param role - User role
 * @param action - Action being attempted (e.g., 'create', 'change state to validated')
 * @returns Localized error message key
 */
export function getRolePermissionError(role: Role, action: string): string {
  // Return translation key that can be used with i18n
  return 'errors.insufficientPermissions';
}

/**
 * Checks if a role can validate orders
 * Only Coex and SuperAdmin can validate (NOT Tablet)
 * @param role - User role
 * @returns true if the role can validate orders
 */
export function canRoleValidateOrders(role: Role): boolean {
  return canRoleUseState(role, OrderState.Validated);
}

/**
 * Interface for state filter options (used in dropdowns/selects)
 */
export interface StateOption {
  value: OrderState;
  label: string;
  [key: string]: any;
}

/**
 * Filters state options by role visibility (for table filters and dropdowns)
 * Uses ROLE_VISIBLE_STATES from roleStateVisibility constants
 *
 * @param role - User role
 * @param options - Array of state options
 * @returns Filtered array containing only visible states for the role
 *
 * @example
 * ```typescript
 * const allOptions = [
 *   { value: OrderState.Open, label: 'Abierta' },
 *   { value: OrderState.Validated, label: 'Validada' },
 * ];
 * const filtered = filterStateOptionsByRole(Role.RoomTeam, allOptions);
 * // returns [{ value: OrderState.Open, label: 'Abierta' }]
 * ```
 */
export function filterStateOptionsByRole(
  role: Role,
  options: StateOption[]
): StateOption[] {
  const visibleStates = getVisibleStatesForRole(role);
  return options.filter((option) => visibleStates.includes(option.value));
}

// ============================================================================
// CONTENT CREATION PERMISSIONS
// ============================================================================

/**
 * States where NO ONE can create content (actions, accidents, communications)
 * These are final/blocked states where only viewing is allowed
 */
export const BLOCKED_STATES_FOR_CONTENT: OrderState[] = [
  OrderState.End,
  OrderState.Annulled,
];

/**
 * States where Tablet role can create content (actions/accidents only)
 * Tablet must have the order in WorkInProgress to create content.
 * V4 (2026-04-21): Recorregut de Vigilància usa el estado `InProgress` cuando
 * hay un tram activo. Es el equivalente semántico de `WorkInProgress` para SR,
 * así que Tablet también puede crear Actuacions basica (FR-9) durante un tram.
 */
export const TABLET_CONTENT_CREATION_STATES: OrderState[] = [
  OrderState.WorkInProgress,
  OrderState.InProgress,
  // RV (2026-07-21, corrección usuario): en un Recorregut la actuación se
  // crea CUANDO SE FINALIZA UN TRAMO (con causa Actuació) — eso deja el RV
  // en `paused` (sin tramo activo), NO en `in-progress`. `paused` es un
  // estado de TRABAJO del RV (entre tramos, la patrulla está en ruta), no un
  // "cambia de estado primero" como en la genérica. Sincroniza con el
  // backend, que ya lo tenía desde 2026-07-04. `paused` no existe en
  // genéricas/campañas, así que esto SOLO afecta al RV.
  OrderState.Paused,
];

/**
 * States where Admin roles (Coex, SuperAdmin) can create content
 * Admins can create in all states except blocked ones
 */
export const ADMIN_CONTENT_CREATION_STATES: OrderState[] = [
  OrderState.Partial,
  OrderState.Open,
  OrderState.Managed,
  OrderState.PendingWork,
  OrderState.WorkInProgress,
  OrderState.PendingValidation,
  OrderState.PendingATValidation, // T66
  OrderState.Validated,
  // RV (2026-07-21, sync con backend 2026-07-04 que ya los tenía): estados
  // propios del RV — Coex/Capataz/SuperAdmin tienen gestión completa también
  // con el recorrido en curso o pausado (paridad con la genérica, donde el
  // admin crea en cualquier estado no terminal). Sin esto el botón "Crear
  // Actuación" del child-orders salía SIEMPRE deshabilitado para gestión.
  OrderState.InProgress,
  OrderState.Paused,
];

/**
 * States where RoomTeam can create communications
 * Same as admin states - they can create comms in all non-blocked states
 */
export const ROOMTEAM_CONTENT_CREATION_STATES: OrderState[] = [
  OrderState.Partial,
  OrderState.Open,
  OrderState.Managed,
  OrderState.PendingWork,
  OrderState.WorkInProgress,
  OrderState.PendingValidation,
  OrderState.PendingATValidation, // T66
  OrderState.Validated,
];

/**
 * Check if a role can create content in a specific order state
 *
 * Rules:
 * 1. NOBODY can create in End/Annulled states
 * 2. RoomTeam can ONLY create communications (not actions/accidents)
 * 3. Tablet can create actions/accidents ONLY in WorkInProgress
 * 4. Admin roles (Coex, SuperAdmin) can create in all non-blocked states
 *
 * @param role - User role
 * @param orderState - Current order state
 * @param contentType - Type of content to create
 * @returns true if the role can create the content type in the given state
 */
export function canCreateContentInState(
  role: Role,
  orderState: OrderState,
  contentType: 'action' | 'accident' | 'communication'
): boolean {
  // Rule 1: NOBODY can create in End/Annulled states
  if (BLOCKED_STATES_FOR_CONTENT.includes(orderState)) {
    return false;
  }

  // Rule 2: RoomTeam can ONLY create communications (not actions/accidents)
  if (role === Role.RoomTeam) {
    if (contentType !== 'communication') {
      return false;
    }
    return ROOMTEAM_CONTENT_CREATION_STATES.includes(orderState);
  }

  // Rule 3: Tablet can create actions/accidents ONLY in WorkInProgress
  if (role === Role.Tablet) {
    // Tablet cannot create communications (handled by @Roles decorator)
    if (contentType === 'communication') {
      return false;
    }
    return TABLET_CONTENT_CREATION_STATES.includes(orderState);
  }

  // Rule 4: Admin roles (Coex, Capataz, SuperAdmin) can create in all non-blocked states
  if (role === Role.Coex || role === Role.Capataz || role === Role.SuperAdmin) {
    return ADMIN_CONTENT_CREATION_STATES.includes(orderState);
  }

  // Rule 5: OperatingTeam cannot create content
  return false;
}

/**
 * Get the reason why content creation is blocked (for UI messages)
 *
 * @param role - User role
 * @param orderState - Current order state
 * @returns Translation key for the block reason, or null if not blocked
 */
export function getContentCreationBlockReason(
  role: Role,
  orderState: OrderState
): string | null {
  // Final states - blocked for everyone
  if (orderState === OrderState.End) {
    return 'orderClosedMessage';
  }
  if (orderState === OrderState.Annulled) {
    return 'orderAnnulledMessage';
  }

  // Tablet specific — necesita un estado de trabajo (WorkInProgress en
  // genéricas; InProgress en el RV — misma constante que canCreateContentInState)
  if (
    role === Role.Tablet &&
    !TABLET_CONTENT_CREATION_STATES.includes(orderState)
  ) {
    return 'tabletOpenStateMessage';
  }

  return null;
}

// ============================================================================
// WORKFLOW STATE FLOW (mirrors backend orders-state-transitions.ts:140-191)
// ============================================================================

/**
 * Valid state transitions (sequential workflow).
 * Only enforced for operational roles (Tablet, RoomTeam).
 * Admin roles (Coex, SuperAdmin) bypass this via canRoleBypassWorkflow().
 */
export const VALID_STATE_FLOW: Partial<Record<OrderState, OrderState[]>> = {
  // Borrador: solo se completa (open) o se descarta (annulled). El back lo
  // exige también a la gestión, antes de su bypass (2026-10-12).
  [OrderState.Partial]: [OrderState.Open, OrderState.Annulled],
  [OrderState.Open]: [
    OrderState.Managed,
    OrderState.PendingWork,
    OrderState.WorkInProgress,
    OrderState.PendingValidation,
    OrderState.Validated,
    OrderState.Annulled,
    OrderState.End,
  ],
  [OrderState.Managed]: [
    OrderState.Open, // Tarea #10: room-team puede reabrir Generic/Campaign desde Managed
    OrderState.PendingWork,
    OrderState.Annulled,
    OrderState.End,
  ],
  [OrderState.PendingWork]: [
    OrderState.WorkInProgress,
    OrderState.Annulled,
    OrderState.End,
  ],
  [OrderState.WorkInProgress]: [
    OrderState.PendingValidation,
    OrderState.Annulled,
    OrderState.End,
  ],
  [OrderState.PendingValidation]: [
    OrderState.Validated,
    OrderState.PendingATValidation,
    OrderState.WorkInProgress,
    OrderState.Annulled,
    OrderState.End,
  ],
  [OrderState.PendingATValidation]: [
    OrderState.Validated,
    OrderState.PendingValidation,
    OrderState.Annulled,
    OrderState.End,
  ],
  [OrderState.Validated]: [OrderState.End, OrderState.Annulled],
  [OrderState.End]: [],
  // Tarea #10: Annulled puede reabrirse a Open SOLO en Generic y Campaign.
  // El filtrado por entity (ENTITY_VALID_STATES + canRoleModifyEntityType
  // del backend) impide que aparezca para Action/Communication/Accident.
  [OrderState.Annulled]: [OrderState.Open],
};

export function canRoleBypassWorkflow(role: Role): boolean {
  // Capataz (main PR #48): clon de Coex también en bypass — perderlo aquí
  // (regresión del merge 2026-07-17, cazada por state-machine-sync.spec.ts)
  // bloqueaba a Capataz reabrir/anular fuera del flujo secuencial.
  return (
    role === Role.Coex || role === Role.Capataz || role === Role.SuperAdmin
  );
}

/**
 * V4 (2026-04-21) — matriz rol × transición específica para SurveillanceRoute.
 * Mirror del backend (`ROLE_ALLOWED_TRANSITIONS` en `surveillance-route-state-validator.service.ts`).
 * Coex/SuperAdmin NO bypassean aquí (diferencia vs. el workflow genérico) —
 * sus transiciones permitidas están listadas explícitamente.
 */
const SR_VALID_STATE_FLOW_BY_ROLE: Partial<
  Record<Role, Partial<Record<OrderState, OrderState[]>>>
> = {
  [Role.Tablet]: {
    [OrderState.Open]: [OrderState.InProgress],
    [OrderState.InProgress]: [OrderState.Paused, OrderState.PendingValidation],
    [OrderState.Paused]: [OrderState.InProgress, OrderState.PendingValidation],
    // Des-entregar (2026-07-21, paridad genérica): el Tablet DUEÑO devuelve su
    // RV a paused (reopen; ownership de equipo en backend, espejo de finish)
    // y reanuda creando tramo. Validar (end) y anular siguen siendo de Coex.
    [OrderState.PendingValidation]: [OrderState.Paused],
    // Terminales inalcanzables para Tablet: deshacer validar/anular es de
    // gestión (2026-08-05). Espejo exacto del backend.
    [OrderState.End]: [],
    [OrderState.Annulled]: [],
  },
  [Role.Coex]: {
    [OrderState.Open]: [OrderState.Annulled],
    [OrderState.InProgress]: [OrderState.Annulled],
    [OrderState.Paused]: [OrderState.Annulled],
    [OrderState.PendingValidation]: [
      OrderState.End,
      // Reabrir/devolver (2026-07-20): a paused, NO in-progress — al entregar
      // se cerró el tramo activo, no hay cronómetro corriendo (espejo backend).
      OrderState.Paused,
      OrderState.Annulled,
    ],
    // Deshacer (2026-08-05): des-validar y des-anular son de gestión.
    // `end` y `annulled` dejaron de ser callejones sin salida — una validación
    // por error congelaba el recorrido para siempre.
    [OrderState.End]: [OrderState.PendingValidation],
    // A `paused`, no a `open`: la anulación cerró los tramos abiertos.
    [OrderState.Annulled]: [OrderState.Paused],
  },
  // Capataz (paridad 2026-07-17): clon EXACTO de Coex — espejo del backend
  // ROLE_ALLOWED_TRANSITIONS (surveillance-route-state-validator.service.ts).
  [Role.Capataz]: {
    [OrderState.Open]: [OrderState.Annulled],
    [OrderState.InProgress]: [OrderState.Annulled],
    [OrderState.Paused]: [OrderState.Annulled],
    [OrderState.PendingValidation]: [
      OrderState.End,
      // Reabrir/devolver (2026-07-20): a paused, NO in-progress — al entregar
      // se cerró el tramo activo, no hay cronómetro corriendo (espejo backend).
      OrderState.Paused,
      OrderState.Annulled,
    ],
    // Deshacer (2026-08-05): des-validar y des-anular son de gestión.
    // `end` y `annulled` dejaron de ser callejones sin salida — una validación
    // por error congelaba el recorrido para siempre.
    [OrderState.End]: [OrderState.PendingValidation],
    // A `paused`, no a `open`: la anulación cerró los tramos abiertos.
    [OrderState.Annulled]: [OrderState.Paused],
  },
  [Role.SuperAdmin]: {
    [OrderState.Open]: [OrderState.InProgress, OrderState.Annulled],
    [OrderState.InProgress]: [
      OrderState.Paused,
      OrderState.PendingValidation,
      OrderState.Annulled,
    ],
    [OrderState.Paused]: [
      OrderState.InProgress,
      OrderState.PendingValidation,
      OrderState.Annulled,
    ],
    [OrderState.PendingValidation]: [
      OrderState.End,
      // Reabrir/devolver (2026-07-20): a paused, NO in-progress — al entregar
      // se cerró el tramo activo, no hay cronómetro corriendo (espejo backend).
      OrderState.Paused,
      OrderState.Annulled,
    ],
    // Deshacer (2026-08-05): des-validar y des-anular son de gestión.
    // `end` y `annulled` dejaron de ser callejones sin salida — una validación
    // por error congelaba el recorrido para siempre.
    [OrderState.End]: [OrderState.PendingValidation],
    // A `paused`, no a `open`: la anulación cerró los tramos abiertos.
    [OrderState.Annulled]: [OrderState.Paused],
  },
  [Role.RoomTeam]: {
    // Read-only para RV (D5).
    [OrderState.Open]: [],
    [OrderState.InProgress]: [],
    [OrderState.Paused]: [],
    [OrderState.PendingValidation]: [],
    [OrderState.End]: [],
    [OrderState.Annulled]: [],
  },
};

/**
 * Tarea #10: replica de backend `isFinalState(state, entityType?)`.
 * Annulled deja de ser final SOLO para Generic y Campaign — para
 * Action/Communication/Accident sigue siendo terminal. Esto coincide
 * con la lógica entity-aware del backend (orders-state-transitions.ts).
 */
export function isFinalState(
  state: OrderState,
  entityType?: OrderType | string
): boolean {
  if (state === OrderState.End) {
    return true;
  }
  if (state === OrderState.Annulled) {
    if (entityType === OrderType.Generic || entityType === OrderType.Campaign) {
      return false;
    }
    return true;
  }
  return false;
}

/**
 * Filters states by workflow transitions.
 * Admin roles (Coex/SuperAdmin) bypass — all states pass through.
 * Operational roles (Tablet/RoomTeam) only see valid transitions from currentState.
 *
 * V4 (2026-04-21): `SurveillanceRoute` usa `SR_VALID_STATE_FLOW_BY_ROLE` dedicada.
 * Coex/SuperAdmin NO bypassean la matriz SR — sus transiciones están explícitas
 * (p.ej. Coex no puede `end → algo` porque `end` es terminal).
 * Tarea #10: con `orderType`, aplica la regla entity-aware de `isFinalState`
 * para que Communications/Accidents anulados no muestren "Abierto" como opción
 * (el backend las rechazaría con BadRequest).
 */
/**
 * Transiciones SR permitidas para un rol desde un estado (matriz D5, espejo
 * del backend ROLE_ALLOWED_TRANSITIONS).
 */
export function getSrAllowedTransitions(
  role: Role,
  currentState: OrderState
): OrderState[] {
  return SR_VALID_STATE_FLOW_BY_ROLE[role]?.[currentState] ?? [];
}

/**
 * Subconjunto MODAL-ABLE de la matriz SR (2026-07-20): transiciones que el
 * ChangeStateModal puede ejecutar sin datos de tramo. Las de timer quedan
 * fuera — necesitan payload que el modal no tiene y romperían los
 * invariantes de tramos:
 *  - open → in-progress: "Iniciar recorrido" (crea el primer tramo con PK/sentido).
 *  - in-progress → paused: pausa con causa (cierra el tramo con causa + PK).
 *  - paused → in-progress: reanudar = "Nuevo tramo" (PK/sentido heredados).
 * Sí son modal-ables: entregar (finish cierra el tramo activo él mismo),
 * validar, reabrir (pending-validation → paused, tramos ya cerrados) y anular
 * (el backend cierra tramos activos en cascada).
 * Gate de la pill de estado del RV: si está vacío, pill de solo lectura.
 */
const SR_TIMER_ONLY_TRANSITIONS: Partial<Record<OrderState, OrderState[]>> = {
  [OrderState.Open]: [OrderState.InProgress],
  [OrderState.InProgress]: [OrderState.Paused],
  [OrderState.Paused]: [OrderState.InProgress],
};

/**
 * Destino de «deshacer» en el RV según el estado de ORIGEN (2026-08-05).
 * Espejo de `SurveillanceRouteStateValidator.reopenTargetFor` del backend.
 *
 *   pending-validation → paused              des-entregar
 *   end                → pending-validation  des-validar
 *   annulled           → paused              des-anular
 *
 * Existe porque el modal enrutaba SOLO por destino, y así `end →
 * pending-validation` caía en la rama de «entregar» (que cierra tramos y llama
 * a `finishSurveillanceRoute`) en vez de en la de deshacer. Con el origen
 * delante, las tres son la misma mutation y no hay ambigüedad.
 *
 * `null` = ese estado no tiene vuelta atrás.
 */
export function getSrUndoTarget(currentState: OrderState): OrderState | null {
  switch (currentState) {
    case OrderState.PendingValidation:
      return OrderState.Paused;
    case OrderState.End:
      return OrderState.PendingValidation;
    case OrderState.Annulled:
      return OrderState.Paused;
    default:
      return null;
  }
}

export function getSrModalTransitions(
  role: Role,
  currentState: OrderState
): OrderState[] {
  const timerOnly = SR_TIMER_ONLY_TRANSITIONS[currentState] ?? [];
  return getSrAllowedTransitions(role, currentState).filter(
    (s) => !timerOnly.includes(s)
  );
}

export function filterStatesByWorkflow(
  currentState: OrderState,
  role: Role,
  states: OrderState[],
  orderType?: string
): OrderState[] {
  // V4 (2026-04-21): SR tiene flujo rol×estado dedicado.
  if (orderType?.toLowerCase?.() === 'surveillanceroute') {
    const roleFlow = SR_VALID_STATE_FLOW_BY_ROLE[role];
    const validTransitions = roleFlow?.[currentState] ?? [];
    return states.filter((s) => validTransitions.includes(s));
  }

  if (canRoleBypassWorkflow(role)) return states;
  // Para roles operacionales: si el estado actual es final entity-aware,
  // no hay transiciones disponibles.
  if (isFinalState(currentState, orderType)) return [];
  const validTransitions = VALID_STATE_FLOW[currentState] ?? [];
  return states.filter((s) => validTransitions.includes(s));
}

// ============================================================================
// ENTITY TYPE RESTRICTIONS PER ROLE
// ============================================================================

/**
 * Entity types each role can change state on.
 * RoomTeam: Generic/Campaign/Communication (their focus is communications)
 * Tablet: Generic/Campaign/Action (their focus is fieldwork)
 * Coex/SuperAdmin: all entity types
 */
const ROLE_ENTITY_TYPES: Record<Role, string[]> = {
  [Role.RoomTeam]: [
    OrderType.Generic,
    OrderType.Campaign,
    OrderType.Communication,
    OrderType.SubOrder,
    OrderType.SurveillanceRoute,
  ],
  [Role.Tablet]: [
    OrderType.Generic,
    OrderType.Campaign,
    OrderType.Action,
    OrderType.SubOrder,
    OrderType.SurveillanceRoute,
  ],
  [Role.Coex]: Object.values(OrderType),
  // Capataz (main PR #48): paridad funcional con Coex en RBAC de estados;
  // la única diferencia es ceguera a budgets (no afecta a este mapa).
  [Role.Capataz]: Object.values(OrderType),
  [Role.SuperAdmin]: Object.values(OrderType),
  [Role.OperatingTeam]: [],
};

/**
 * Check if a role can change the state of a specific entity type.
 * Used to disable the "Change state" button in the UI.
 */
export function canRoleChangeEntityState(
  role: Role,
  entityType: string
): boolean {
  const allowed = ROLE_ENTITY_TYPES[role] ?? [];
  return allowed.includes(entityType);
}

// ============================================================================
// DESTINOS DISPONIBLES — punto ÚNICO de verdad de la UI
// ============================================================================

/**
 * Estados que NUNCA se ofrecen como destino manual, sea cual sea el rol o la
 * entidad. No es una regla de permisos: es que no se alcanzan a mano.
 *
 *  - `Partial`: lo escribe el asistente mientras construye la entidad; pasa a
 *    `Open` solo al guardarla. El backend aceptaría `partial → open` de un
 *    Tablet, pero nadie debe poder «abrir» a mano un borrador a medias.
 *
 * `Transferred` estaba aquí hasta el 2026-08-31, cuando el estado se retiró
 * entero: no lo escribía ningún camino y no había una sola fila con él ni en
 * beta ni en producción. El traspaso se registra con `isTransferred`.
 */
const NEVER_MANUAL_TARGET_STATES: OrderState[] = [OrderState.Partial];

/**
 * Destinos manuales de una Actuación. Vivía suelto dentro del
 * `ChangeStateModal` (`optionsFiltered`), que era una CUARTA capa de reglas
 * fuera del espejo: ni estaba aquí, ni en el backend, ni la cubría el
 * tripwire. Al traerlo aquí queda ejercido por
 * `getAvailableStateTransitions` y vigilado por
 * `tests/unit/rbac/state-transition-availability.spec.ts`.
 *
 * Una Actuación no se «gestiona» ni «se pone en curso»: se valida, se marca
 * pendiente de certificar, se anula o se cierra. Los estados de despacho
 * (`managed`/`pending-work`/`work-in-progress`/`pending-validation`) son de la
 * OT padre y ni siquiera son válidos para la entidad (ENTITY_VALID_STATES).
 */
const ACTION_MANUAL_TARGET_STATES: OrderState[] = [
  OrderState.Annulled,
  OrderState.End,
  OrderState.Validated,
  OrderState.PendingCertification,
];

/**
 * Estados a los que `role` puede llevar una entidad `entityType` que hoy está
 * en `currentState`. **Punto único**: lo consume el desplegable del
 * `ChangeStateModal` y también los sitios que deciden si pintar el gesto
 * (chip/botón «Cambiar estado»).
 *
 * Antes cada sitio decidía por su cuenta con `canRoleChangeEntityState()`, que
 * responde «¿este rol toca este TIPO de entidad?» y no «¿puede moverla desde
 * donde está?». Para `tablet × Action` la primera dice sí y la segunda no, así
 * que se ofrecía el gesto y el modal salía vacío: un callejón sin salida.
 *
 * Devuelve `[]` cuando no hay ningún destino ⇒ **no ofrezcas el gesto**
 * (`canRoleChangeStateOf`).
 */
export function getAvailableStateTransitions(
  role: Role | string | undefined | null,
  entityType: string | undefined | null,
  currentState: OrderState | string | undefined | null
): OrderState[] {
  if (!currentState) return [];

  const universe = Object.values(OrderState).filter(
    (s) => !NEVER_MANUAL_TARGET_STATES.includes(s)
  );

  // Rol desconocido: el modal enseñaba TODO como red de seguridad. Se conserva
  // el comportamiento para no cambiar nada fuera del alcance de este arreglo.
  if (!role || !Object.values(Role).includes(role as Role)) {
    return universe.filter((s) => s !== currentState);
  }

  // Barrera 1 — ¿este rol toca este tipo de entidad?
  if (entityType && !canRoleChangeEntityState(role as Role, entityType)) {
    return [];
  }

  const current = currentState as OrderState;

  // Barrera 2 — estados que el rol tiene permitidos (con la matriz dedicada
  // del RV y el recorte Bug #29 dentro).
  const roleAllowed = getAllowedStatesForRoleAndType(
    role as Role,
    entityType || ''
  );

  // Barrera 3 — estados que esa entidad admite (espejo de
  // VALID_STATES_BY_ENTITY_TYPE del backend).
  const entityValid = entityType
    ? getValidStatesForEntityType(entityType as OrderType)
    : universe;

  // Barrera 4 — flujo: ¿se llega desde donde estamos?
  const candidates = universe.filter(
    (s) => roleAllowed.includes(s) && entityValid.includes(s)
  );
  const workflowAllowed = filterStatesByWorkflow(
    current,
    role as Role,
    candidates,
    entityType || undefined
  );

  // Barrera 5 — RV: del flujo D5 solo lo que el modal puede ejecutar sin
  // datos de tramo (iniciar/pausar/reanudar son gestos del cronómetro).
  const srModalAllowed =
    entityType === OrderType.SurveillanceRoute
      ? getSrModalTransitions(role as Role, current)
      : null;

  // Barrera 6 — política de destinos manuales por entidad.
  return candidates.filter((s) => {
    if (!workflowAllowed.includes(s)) return false;
    if (srModalAllowed !== null && !srModalAllowed.includes(s)) return false;
    if (entityType === OrderType.Action) {
      // Una Actuación anulada solo puede reabrirse (y solo quien bypassa el
      // flujo llega hasta aquí con `open` entre los candidatos).
      if (current === OrderState.Annulled) return s === OrderState.Open;
      // Una Actuación PARCIAL es un borrador: se completa por su asistente,
      // que exige los datos, y a mano solo se descarta. Validarla o cerrarla
      // desde aquí la haría contar en horas e informes vacía (en el RV no
      // tiene ni capítulo). El resumen ya no ofrece «Validar» ni «Pendiente
      // de certificar» en una parcial; este modal era la única puerta que
      // quedaba. Regla de interfaz: `SaveAction` del asistente no pasa por
      // aquí. Diagnóstico 2026-10-08 § 11.6.
      if (current === OrderState.Partial) return s === OrderState.Annulled;
      return ACTION_MANUAL_TARGET_STATES.includes(s);
    }
    return true;
  }).filter((s) => s !== current);
}

/**
 * ¿Hay algo a lo que llevar esta entidad? Gate de la UI: si es `false`, el
 * chip/botón de «Cambiar estado» NO debe ofrecerse (ni pintarse pulsable, ni
 * abrir el modal en vacío, ni tragarse el clic en silencio).
 *
 * Invariante que vigila `state-transition-availability.spec.ts`:
 * **gesto ofrecido ⇒ al menos un destino**.
 */
export function canRoleChangeStateOf(
  role: Role | string | undefined | null,
  entityType: string | undefined | null,
  currentState: OrderState | string | undefined | null
): boolean {
  return getAvailableStateTransitions(role, entityType, currentState).length > 0;
}

// ============================================================================
// BUDGETS VISIBILITY (main PR #48: rol Capataz ciego a presupuestos)
// ============================================================================

/**
 * Roles CIEGOS a partidas/presupuestos. Lista de bloqueo (no allowlist) para
 * que los roles existentes conserven exactamente su comportamiento legacy
 * (RoomTeam read-only en validación, etc.).
 *
 * Espejo funcional del backend: los @Roles de budget-items/budget-categories/
 * action-budget-items NUNCA incluyen estos roles (tripwire backend
 * capataz-budget-tripwire.unit.spec.ts).
 *
 * Tablet añadido 2026-07-16 (bug): el resolver actionBudgetItem es
 * @Roles(SuperAdmin, Coex, OperatingTeam) — SIN Tablet — pero el front solo
 * bloqueaba a Capataz. Un Tablet que abría el RESUMEN de una actuación con
 * partidas lanzaba GetActionBudgetItem, recibía Forbidden y el selector
 * tiraba la vista entera ("No se pudo cargar la actuación"). El redirect del
 * wizard (BudgetItemsFormPage) cubría el paso Partidas, pero no el resumen
 * ni el PDF. RESUMEN-PERMISOS-POR-ROL § 11: TODAS las queries/mutations de
 * budgets → Tablet: No. Tripwire: tests/e2e/specs/rbac/
 * tablet-budget-blindness.spec.ts.
 */
const BUDGET_BLIND_ROLES: ReadonlySet<string> = new Set([
  Role.Capataz,
  Role.Tablet,
]);

/**
 * ¿Puede el rol ver CUALQUIER cosa de partidas/presupuestos?
 * false → no renderizar UI de budgets NI lanzar queries de budgets
 * (el backend respondería Forbidden y rompería la vista).
 */
export function roleCanViewBudgets(role?: string | null): boolean {
  return !role || !BUDGET_BLIND_ROLES.has(role);
}

/**
 * Roles que pueden editar el CONTENIDO de una Order en estados intermedios,
 * no solo en `open`.
 *
 * Vivía escrita a mano dentro de `ShowOT/WorkOrder/index.tsx` (el lápiz
 * canónico). Se trae aquí, junto al resto del RBAC de front, porque desde
 * 2026-08-04 la comparten dos consumidores: ese mismo lápiz y el botón
 * "Editar comunicación" de las tablas de OT. Dos copias de esta lista es
 * exactamente el drift que la nota de cabecera de este fichero advierte.
 *
 * NO confundir con `SR_PRIVILEGED_EDIT_ROLES` de `utils/surveillanceRoute.ts`:
 * coinciden hoy en contenido pero son dominios distintos (campos de Order vs.
 * campos de SurveillanceRoute) y no deben derivar el uno del otro.
 */
export const PRIVILEGED_EDIT_ROLES: readonly Role[] = [
  Role.SuperAdmin,
  Role.Coex,
  Role.Capataz,
  Role.RoomTeam,
];

/**
 * ¿Puede este rol abrir la edición de una Order que está en este estado?
 *
 * Regla única, la del lápiz canónico de `ShowOT/WorkOrder`:
 * - `end` y `annulled` son terminales: nadie edita, ni siquiera SuperAdmin.
 * - En `open` edita cualquier rol que llegue al botón.
 * - Fuera de `open`, solo `PRIVILEGED_EDIT_ROLES`, más Tablet en
 *   `work-in-progress` (la patrulla trabajando sobre su propia OT).
 *
 * Recibe el estado en CRUDO. Los valores del enum `variant` de `SpecialTag` y
 * los de `OrderState` son el mismo string (`'open'`, `'annulled'`…), así que
 * sirve tanto para una fila de tabla (`row.state`) como para el `tag` de
 * `ShowOT`. Sin estado (fila a medio cargar, offline) devuelve `false`:
 * preferimos un botón deshabilitado a un callejón sin salida dentro del
 * formulario.
 */
export function canEditOrderRow(
  role?: Role | string | null,
  state?: OrderState | string | null
): boolean {
  if (!role || !state) return false;
  if (state === OrderState.End || state === OrderState.Annulled) return false;
  if (state === OrderState.Open) return true;
  if (role === Role.Tablet) return state === OrderState.WorkInProgress;
  return PRIVILEGED_EDIT_ROLES.includes(role as Role);
}
